import React, {useState, useMemo, useEffect, useCallback } from "react";
import { SearchWidget, Button, RadioButton, ComponentLoader, StyledLink, IconsCircleBorder, Span, Tooltip } from "@netapp/bxp-design-system-react";
import {useDispatch, useSelector} from "react-redux";
import styles from "./../MainHeader.module.scss";
import serviceConnectorMenuStyles from "./ConnectorMenu.module.scss";
import SlidingWidget, {CaptionButton, SlidingWidgetFooter} from "./SlidingWidget"
import classNames from "classnames";
import { ConnectorNoCircleIcon } from "@netapp/bxp-design-system-react/icons/monochrome";
import { ArrowUpIcon } from "@netapp/bxp-style/react-icons/Navigation";
import ListMenuContent from "./ListMenuContent"
import _ from "lodash";
import ExternalLink from "components/ExternalLink";
import typography from "@netapp/bxp-design-system-react/styles/typography.module.scss"
import { providersMap } from "utils/providers";
import buttons from "@netapp/bxp-design-system-react/styles/buttons.module.scss"
import { Trans, useTranslation } from "react-i18next";
import { allMenus as i18nNamespace } from "../../../../i18n/namespace";
import { useLocation } from "react-router";
import AgentIcon from "./AgentIcon";
import {AGENT_TROUBLESHOOTING_GUIDE_URL} from "utils/app/constants";

const Status = ({status, t}) => {
    if(status === 'inactive') {
        return <span className={classNames(serviceConnectorMenuStyles["item-detail"], serviceConnectorMenuStyles['item-status'])}>
            <span className={serviceConnectorMenuStyles[`box-${status}`]}/>
            {t(status)}
            <Tooltip>
                <Span>
                    <Trans 
                        i18nKey="agent_unreachable_message" 
                        t={t} 
                        components={{ 
                            learnMoreLink: <ExternalLink variant="text" href={AGENT_TROUBLESHOOTING_GUIDE_URL} />
                        }} 
                    />
                </Span>
            </Tooltip>
        </span>
   } else {
     return <span className={classNames(serviceConnectorMenuStyles["item-detail"], serviceConnectorMenuStyles['item-status'])}>
        <span className={serviceConnectorMenuStyles[`box-${status}`]}/>
        {t(status)}</span>
   }
};

const changeVmTypeString = (provider,t) => {
    if(provider === "aws") {
        return t("change_instance_required")
    } else if (provider === "gcp") {
        return t("change_machine_type_required")
    } else if (provider === "azure") {
        return t("change_vm_type_required")
    } else {
        return t("increase_vm_performance_required")
    }
}

const ServiceConnectorItem = React.memo(({ index, style, data: {agents, setSelectedAgent, selectedAgent} }) => {
    const dispatch = useDispatch();
    const agent = agents[index];
    const isSelected = selectedAgent === agent;
    const {provider, region, status, primaryCallbackUri}  = agent;
    const darkSite = process.env.REACT_APP_IS_DARK === 'true';
    const useDockerInfra = agent.agent?.useDockerInfra || darkSite;
    const isLocalUi = process.env.REACT_APP_IS_DARK !== "true" && process.env.REACT_APP_IS_SAAS !== "true";
    const { t } = useTranslation(i18nNamespace);
    const location = useLocation();
    const [redirectionURI, setRedirectionURI] = useState(null);
    const permissions = useSelector(state => state.tenancy.permissions);

    useEffect(() => {
        if(!_.isNil(primaryCallbackUri)) setRedirectionURI(primaryCallbackUri + (location?.pathname ?? '') + (location?.search ?? ''));
    }, [location, primaryCallbackUri])

    return <div style={style} className={serviceConnectorMenuStyles['item']}>
        <RadioButton disabled={status !== "active" || process.env.REACT_APP_IS_SAAS !== "true"} className={classNames(serviceConnectorMenuStyles['checkbox'], {[serviceConnectorMenuStyles['checkbox-disabled']]: status !== "active"})} onChange={() => {
            if(isSelected) {
                return;
            }
            setSelectedAgent(agent)
        }} checked={isSelected} aria-label={agent.name} data-testid={`radio-agent-${agent.name}`}>
            <div className={serviceConnectorMenuStyles["top-line-info"]}>
            <span title={agent.name}>{agent.name} </span>
            {(process.env.REACT_APP_IS_SAAS === "true" || (isLocalUi && !isSelected)) && <ExternalLink className={classNames(buttons.text, serviceConnectorMenuStyles.menu,`${!primaryCallbackUri ? serviceConnectorMenuStyles['disable-pointers'] : 'none'}`)} trackCategory="Connectors" trackLabel="Go to Local UI" disabled={!primaryCallbackUri} href={redirectionURI} >
                {t("go_to_local_ui")} <ArrowUpIcon size={16} style={{ marginLeft: 3,transform:'rotate(45deg)'}}/>
            </ExternalLink>}
            {permissions.editConnectorUris && (process.env.REACT_APP_IS_DARK === "true" || isLocalUi) && isSelected && <StyledLink
                variant="text"
                style={{ margin: "0px 5px 0px auto" }}
                to={{
                    pathname: `/agents-mgmt/${agent.agentId}/edit`,
                    state: { selectedRow: agent }
                }}
                onClick={() => dispatch({ type: "MENU:CLOSE-WIDGET" })}
            >
                {t("edit_connector")}
            </StyledLink>}
            </div>
        </RadioButton>
        <div className={serviceConnectorMenuStyles["details-row"]}>
            <span className={serviceConnectorMenuStyles["item-detail"]}>{providersMap[provider]?.label || provider}</span>
            <span className={serviceConnectorMenuStyles["item-detail"]}>{region || "-"}</span>
            <Status status={status} t={t}/>
        </div>
        {!useDockerInfra && <div className={serviceConnectorMenuStyles['need-upgrade-notice']}>
            <span className={classNames(typography.error, typography.b)}>{t("notice")} </span> {changeVmTypeString(provider,t)} <Button variant="text" onClick={() => {
            dispatch({
                type: "CURRENT-CONNECTOR:OPEN-WEAK-CONNECTOR-DIALOG",
                payload: {
                    provider
                },
                track: "from-connector-menu"
            })
        }}>{t("learn_more")}</Button>
        </div>}
    </div>;
});

const ServiceConnectorMenuFooter = ({activeServiceConnector, selectedServiceConnector}) => {
    const dispatch = useDispatch();

    return <SlidingWidgetFooter onSwitch={() => {
        if(activeServiceConnector === selectedServiceConnector){
            return;
        }

        dispatch({
            type: "TENANCY:SET-AGENT",
            payload: {
                agentId: selectedServiceConnector.agentId
            },
            track: true
        });

        dispatch({
            type: "MENU:CLOSE-WIDGET"
        })
    }} onClose={() => {
        dispatch({
            type: "MENU:CLOSE-WIDGET"
        })
    }}/>
};

const noAgents = [];
const MenuContent = React.memo(({setSelectedAgent, selectedAgent, removeConnector}) => {
    // connectorChanges: Remove this comment once the agent association is approved. 
    // const agents = useSelector(state => isTenancyV4 ? state.tenancy?.selectedScope?.associatedAgents : state.tenancy.agents) || noAgents;
    const agents = useSelector(state => state.tenancy.agents) || noAgents;
    const agentStatus = useSelector(state => state.tenancy.agentStatus);
    const errorReason = useSelector(state => state.tenancy.agentErrorReason);
    const activeAgent = useSelector(state => state.tenancy.selectedAgent);
    const [activeFilter, setActiveFilter] = useState("");
    const permissions = useSelector(state => state.tenancy.permissions);
    const { t } = useTranslation(i18nNamespace);
    const dispatch = useDispatch();

    const filteredAgents = useMemo(() => {
        if(activeFilter) {
            const lowerCasedFilter = _.toLower(activeFilter);
            return _.filter(agents, agent => {
                return _.includes(_.toLower(agent.name), lowerCasedFilter) || agent.status === lowerCasedFilter || agent.provider === lowerCasedFilter || agent.region === lowerCasedFilter;
            })
        } else {
            return agents;
        }
    }, [activeFilter, agents]);

    if(removeConnector) {
        return ""
    }

    const template = agentStatus === "SUCCESS" && agents.length > 0 ? "65px 1fr" : "1fr";

    return <div style={{display: "grid", gridTemplateRows: template, height: "100%"}}>
        {agentStatus === "PENDING" && <div>
            <ComponentLoader className={styles["menu-loader"]}/>
        </div>}
        {agentStatus === "SUCCESS" && agents.length === 0 && <div className={serviceConnectorMenuStyles['no-connector']}>
            <Span className={serviceConnectorMenuStyles['icon-position']}>
                <IconsCircleBorder>
                    <ConnectorNoCircleIcon color="disabled"/>
                </IconsCircleBorder>
            </Span>
            <span className={classNames(typography.body16, typography["text-disabled"])}>{t("no_existing_connectors")}</span>
            {permissions.addConnector && <StyledLink large onClick={() => dispatch({ type: "MENU:CLOSE-WIDGET" })} to={location => ({...location, hash: "addConnector/provider", state: {from: "connectorMenu"}})}>{t("create_your_first_connector")}</StyledLink>}
        </div>}
        {agentStatus === "SUCCESS" && agents.length > 0 && <>
            <SearchWidget color="secondary" className={serviceConnectorMenuStyles['search-widget-container']} alwaysOpen={true} placeholder={t("search_blue_xp_connectors")} setFilter={setActiveFilter} widgetClassName={serviceConnectorMenuStyles['search-widget']}/>
            <ListMenuContent
                itemSize={116}
                label={t("connectors_lower")}
                errorReason={errorReason}
                heightOffset={290 - (process.env.REACT_APP_IS_SAAS === "true" ? 0 : 65)}
                Item={ServiceConnectorItem}
                status={agentStatus}
                activeItem={activeAgent}
                selectedItem={selectedAgent}
                setSelectedItem={setSelectedAgent}
                items={filteredAgents}
                itemData={{agents: filteredAgents, setSelectedAgent, selectedAgent, permissions}}
            />
        </>}
    </div>
});

const MenuHeaderButtons = React.memo(() => {
    const permissions = useSelector(state => state.tenancy.permissions);
    const { t } = useTranslation(i18nNamespace);
    const dispatch = useDispatch();

    // Only render for SaaS environments
    if (process.env.REACT_APP_IS_SAAS !== "true") {
        return null;
    }


    const containerStyles = {
        height: 24,
        display: "inline-flex",
        alignItems: "center"
    };

    // Determine manage connectors link based on workflow flag
    const manageConnectorsLink = "/agents-mgmt"

    return (
        <div style={containerStyles}>

            <StyledLink
                type="text"
                to={manageConnectorsLink}
                onClick={() => dispatch({ type: "MENU:CLOSE-WIDGET" })}
            >
                {t("manage_connectors")}
            </StyledLink>
        </div>
    );
});

/**
 *  This component is temporarily added with hardcoded svg until we get agent icon from design system
 */
const AgentMenuButton = React.memo(({ buttonRef, onClick, onKeyDown, className, menuTitle, activeAgent }) => {
    const { t } = useTranslation(i18nNamespace);
    const agentName = activeAgent?.name ? `${t("selected_agent")}: ${activeAgent.name}` : t("no_agent_selected");

    return <button ref={buttonRef} onMouseDown={e => e.preventDefault()} onClick={onClick} onKeyDown={onKeyDown} className={classNames(styles["trigger"], serviceConnectorMenuStyles["agent-icon-topnav"], className)} aria-label={menuTitle}>
        {/* here we import design system icon once available  */}
        <AgentIcon />
        <Tooltip>{agentName}</Tooltip>
    </button>
});

export default () => {
    const activeAgent = useSelector(state => state.tenancy.selectedAgent);
    // connectorChanges: Remove this comment once the agent association is approved. 
    // const agents = useSelector(state => isTenancyV4 ? state.tenancy?.selectedScope?.associatedAgents : state.tenancy.agents) || [];
    const agents = useSelector(state => state.tenancy.agents) || [];
    const agentStatus = useSelector(state => state.tenancy.agentStatus);
    const [selectedAgent, setSelectedAgent] = useState(activeAgent);
    const isAddConnectorMenuOpen = useSelector(state => state.app.addServiceConnectorWidgetOpen);
    const removeConnector = useSelector(state => state.menu.removeConnector);
    const { t } = useTranslation(i18nNamespace);

    // Memoize to prevent unnecessary re-renders
    const menuFooter = useMemo(() => {
        return process.env.REACT_APP_IS_SAAS !== "true" ? undefined : (() => {
            if (removeConnector || (agentStatus === "SUCCESS" && agents.length === 0)) {
                return ""
            } else {
                return <ServiceConnectorMenuFooter activeServiceConnector={activeAgent} selectedServiceConnector={selectedAgent} />
            }
        });
    }, [removeConnector, agentStatus, agents.length, activeAgent, selectedAgent]);

    const canClose = useCallback(() => {
        return !isAddConnectorMenuOpen;
    }, [isAddConnectorMenuOpen]);

    const agentTriggerButton = useCallback((props) => (
        <AgentMenuButton {...props} activeAgent={activeAgent} />
    ), [activeAgent]);

    return (
        <SlidingWidget
            widgetKey="connectors"
            menuTitle={t("connectors")}
            triggerClassName={styles['widget-button']}
            triggerActiveClassName={[styles['widget-button-active']]}
            TriggerButton={agentTriggerButton}
            MenuFooter={menuFooter}
            HeaderContents={MenuHeaderButtons}
            canClose={canClose}
        >
            <MenuContent removeConnector={removeConnector} setSelectedAgent={setSelectedAgent} selectedAgent={selectedAgent} />
        </SlidingWidget>
    );
};
